import { useCallback, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import AppLayout from "../../components/AppLayout";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Select } from "../../components/Shared";
import { DeleteSourceDialog, EditNameForm } from "../SourceEditDelete";
import { DATASET_KINDS, KIND_HINT } from "../../datasetKinds";
import { useApi } from "../../useApi";
import { authorName, editedBy } from "./author";
import ColumnsTab from "./ColumnsTab";
import FairTab, { ReadyBadge } from "./FairTab";
import FilesTab from "./FilesTab";
import MetadataTab from "./MetadataTab";
import ProvenanceTab from "./ProvenanceTab";

// /datasets/:datasetId/:tab?  — tab is "files" (default), "metadata" (the dataset's additional
// fields and the JSON-LD / RO-Crate downloads), "columns", "provenance" or "fair" (the FAIR
// self-check and the "ready for integration" mark). There is no Protocols tab since 2026-10-20:
// a protocol is a file of the dataset with the role protocol, set on the Files tab.
//
// Every tab component receives the same props (the contract feature work relies on):
//   dataset        DatasetOut from GET /api/datasets/{id}
//                  {id, study_id, study_name, project_id, project_name,
//                   study_created_by{id, username, full_name} (the study's author),
//                   uuid, kind (primary|derived), name, description,
//                   file_count, can_edit, ready, ready_state (ready|stale|not_ready), ...}
//   files          DataFileOut[] from GET /api/datasets/{id}/files
//                  [{id, filename, size, md5, sha256, checksum, media_type, is_tabular, row_count, column_count,
//                    inference_error, created_at, role (raw|attachment|protocol), role_set (set by hand, or null)}]
//   canEdit        dataset.can_edit (the server's answer: the study's author, a curator of
//                  the project or an admin)
//   reloadDataset  () => void   re-fetch the dataset (e.g. after saving its fields)
//   reloadFiles    () => void   re-fetch the file list (e.g. after re-inference)

export const TABS = [
  { key: "files", label: "Files", Component: FilesTab },
  { key: "metadata", label: "Metadata", Component: MetadataTab },
  { key: "columns", label: "Columns", Component: ColumnsTab },
  { key: "provenance", label: "Provenance", Component: ProvenanceTab },
  { key: "fair", label: "FAIR", Component: FairTab },
];

// The dataset's kind: a badge for readers, a select that PATCHes the dataset for its editors.
function KindControl({ dataset, canEdit, reloadDataset }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  if (!canEdit) {
    return <Badge color={dataset.kind === "derived" ? "amber" : "gray"}>{dataset.kind}</Badge>;
  }
  const change = async e => {
    const kind = e.target.value;
    if (!kind || kind === dataset.kind) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/datasets/${dataset.id}`, { kind });
      reloadDataset();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap", maxWidth: "100%" }}>
      {/* 300 px wide, but never wider than the line: at 320 px (400 % zoom) a fixed width
          pushed the page sideways (reflow check 2026-10-06). */}
      <span data-kind-select style={{ width: 300, maxWidth: "100%" }}>
        <Select aria-label="Dataset kind" title={KIND_HINT} options={DATASET_KINDS} value={dataset.kind}
                disabled={saving} onChange={change} />
      </span>
      {error && <span role="alert" style={{ color: "#b91c1c" }}>{error.message || String(error)}</span>}
    </span>
  );
}

export default function DatasetPage() {
  const { datasetId, tab = "files" } = useParams();
  const navigate = useNavigate();
  const dataset = useApi(`/datasets/${datasetId}`);
  const files = useApi(`/datasets/${datasetId}/files`);
  const tabRefs = useRef({});
  const [editing, setEditing] = useState(false);     // Edit dataset (name, description)
  const [deleting, setDeleting] = useState(false);   // the Delete dataset dialog
  const d = dataset.data;
  const active = TABS.find(t => t.key === tab) || TABS[0];
  const { reload: reloadDataset } = dataset;
  const { reload: reloadFilesOnly } = files;
  const reloadFiles = useCallback(() => { reloadFilesOnly(); reloadDataset(); }, [reloadFilesOnly, reloadDataset]);

  const onKeyDown = e => {
    const i = TABS.findIndex(t => t.key === active.key);
    let j = null;
    if (e.key === "ArrowRight") j = (i + 1) % TABS.length;
    if (e.key === "ArrowLeft") j = (i - 1 + TABS.length) % TABS.length;
    if (j === null) return;
    e.preventDefault();
    navigate(`/datasets/${datasetId}/${TABS[j].key}`);
    const el = tabRefs.current[TABS[j].key];
    if (el) el.focus();
  };

  const Component = active.Component;
  return (
    <AppLayout
      title={d ? d.name : "Dataset"}
      crumbs={[{ label: "Projects", to: "/projects" },
               d ? { label: d.project_name, to: `/projects/${d.project_id}` } : { label: "…" },
               d ? { label: d.study_name, to: `/studies/${d.study_id}` } : { label: "…" },
               { label: d ? d.name : "…" }]}
    >
      <ErrorNote error={dataset.error} />
      {dataset.loading && !d && <Loading />}
      {d && (
        <>
          <Card style={{ marginBottom: 20, padding: "16px 22px" }}>
            {d.description && <p style={{ margin: "0 0 8px", fontSize: 14, color: "#374151" }}>{d.description}</p>}
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#6b7280", flexWrap: "wrap" }}>
              <span>Study by</span><Badge color="blue">{authorName(d.study_created_by)}</Badge>
              <span>· Kind</span>
              <KindControl dataset={d} canEdit={!!d.can_edit} reloadDataset={reloadDataset} />
              <span>· {d.file_count} file{d.file_count === 1 ? "" : "s"}</span>
              {d.ready_state && (
                <Link to={`/datasets/${datasetId}/fair`} aria-label={`Readiness: ${d.ready_state.replace("_", " ")}`}
                      style={{ textDecoration: "none" }}>
                  <ReadyBadge state={d.ready_state} />
                </Link>
              )}
              {!d.can_edit && <span>· read-only for you: {editedBy(d.study_created_by)}</span>}
            </div>
            {d.can_edit && !editing && (
              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Btn variant="secondary" onClick={() => setEditing(true)}>Edit dataset</Btn>
                <Btn variant="danger" onClick={() => setDeleting(true)}>Delete dataset</Btn>
              </div>
            )}
            {editing && (
              <EditNameForm kind="dataset" path={`/datasets/${d.id}`} item={d}
                            onSaved={() => { setEditing(false); reloadDataset(); }} onCancel={() => setEditing(false)} />
            )}
          </Card>
          {deleting && (
            <DeleteSourceDialog kind="dataset" path={`/datasets/${d.id}`} item={d}
                                onDeleted={() => navigate(`/studies/${d.study_id}`)} onClose={() => setDeleting(false)} />
          )}

          <div role="tablist" aria-label="Dataset sections" onKeyDown={onKeyDown}
               style={{ display: "flex", flexWrap: "wrap", gap: 4, borderBottom: "1px solid #e5e7eb", marginBottom: 20 }}>
            {TABS.map(t => {
              const selected = t.key === active.key;
              return (
                <Link
                  key={t.key}
                  ref={el => { tabRefs.current[t.key] = el; }}
                  role="tab"
                  id={`tab-${t.key}`}
                  aria-selected={selected}
                  aria-controls={`panel-${t.key}`}
                  tabIndex={selected ? 0 : -1}
                  to={`/datasets/${datasetId}/${t.key}`}
                  style={{
                    padding: "9px 16px", fontSize: 14, fontWeight: selected ? 700 : 500, textDecoration: "none",
                    color: selected ? "var(--dp-accent-dark)" : "#374151",
                    borderBottom: selected ? "3px solid var(--dp-accent)" : "3px solid transparent", marginBottom: -1,
                  }}
                >
                  {t.label}
                </Link>
              );
            })}
          </div>
          <div role="tabpanel" id={`panel-${active.key}`} aria-labelledby={`tab-${active.key}`}>
            <ErrorNote error={files.error} />
            <Component dataset={d} files={Array.isArray(files.data) ? files.data : []} canEdit={!!d.can_edit}
                       reloadDataset={reloadDataset} reloadFiles={reloadFiles} />
          </div>
        </>
      )}
    </AppLayout>
  );
}
